import React, { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Button as CompatButton, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Pagination, PaginationItem, PaginationLink } from '@/components/ui-compat'
import { Button } from '@/components/ui/button'
import { Badge as ShadcnBadge } from '@/components/ui/badge'
import { Plus, FileText, Trash2, X, ChevronLeft, ChevronRight, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { snagStatusVariant, snagPriorityVariant } from '@/lib/status-variants'

import Grid from '../../../components/grid'
import Loading from '../../../components/Loading'
import SnagsListSkeleton from './SnagsListSkeleton'
import SnagsFilterBar from './SnagsFilterBar'
import SnagModal from './SnagModal'
import PdfExportModal from './PdfExportModal'
import { useTranslation } from 'react-i18next'
import useSnagsFilters, { ALL_STATUSES, ALL_PRIORITIES } from '../../../hooks/useSnagsFilters'
import useRouter from '../../../hooks/useRouter'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { toastr } from 'react-redux-toastr'

interface Snag {
  id: string
  number: number
  displayNumber: string
  title: string
  description: string | null
  status: string
  priority: string
  dueDate: string | null
  resolvedAt: string | null
  verifiedAt: string | null
  pinX: number | null
  pinY: number | null
  infrastructureType: string | null
  floor: {
    id: string
    name: string
    level: number
    building: {
      id: string
      name: string
      code: string | null
    }
  }
  assignedTo: {
    id: string
    name: string
    email: string
  } | null
  createdBy: {
    id: string
    name: string
  }
  files: {
    id: string
    filename: string
  }[]
  createdAt: string
}

const SnagsList: React.FC = () => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const currentProject = meData?.me?.currentProject
  const projectId = currentProject?.id

  const { canCreate, canEdit, canDelete, canExport } = useModuleActions('ISSUES', projectId)

  const [selection, setSelection] = useState<string[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [editingSnagId, setEditingSnagId] = useState<string | null>(null)
  const [pdfExportModalOpen, setPdfExportModalOpen] = useState(false)
  const [exportSelectedModalOpen, setExportSelectedModalOpen] = useState(false)
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 25
  const { history, match, location } = useRouter()

  // Auto-open snag modal when ?openSnagId=... is in URL (e.g. from meeting links)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const id = params.get('openSnagId')
    if (id && !modalOpen) {
      setEditingSnagId(id)
      setModalOpen(true)
      params.delete('openSnagId')
      const next = params.toString()
      history.replace(`${match.url}${next ? `?${next}` : ''}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // Handler to navigate to blueprint view for pin placement
  const handleChangePinLocation = (snagId: string, floorId: string) => {
    // Close the modal
    setModalOpen(false)
    setEditingSnagId(null)

    // Navigate to blueprint tab with editPinSnagId param
    const params = new URLSearchParams(location.search)
    params.set('tab', 'blueprint')
    params.set('floor', floorId)
    params.set('editPinSnagId', snagId)
    history.push(`${match.url}?${params.toString()}`)
  }

  // Handler to navigate to blueprint view, read-only centered on the pin
  const handleShowOnBlueprint = (snagId: string, floorId: string) => {
    setModalOpen(false)
    setEditingSnagId(null)
    const params = new URLSearchParams(location.search)
    params.set('tab', 'blueprint')
    params.set('floor', floorId)
    params.set('focusSnagId', snagId)
    history.push(`${match.url}?${params.toString()}`)
  }

  // Fetch buildings for floor selection
  const { data: buildingsData } = useQuery(Query.BUILDINGS, {
    variables: { projectId },
    skip: !projectId,
  })

  // Fetch snag settings for infrastructure types
  const { data: settingsData } = useQuery(Query.SNAG_SETTINGS, {
    variables: { projectId },
    skip: !projectId,
  })

  const buildings = buildingsData?.buildings || []
  const infrastructureTypes: string[] = settingsData?.snagSettings?.infrastructureTypes || []

  // URL-persisted filters
  const [filters, setFilters] = useSnagsFilters({
    defaultInfrastructureTypes: infrastructureTypes,
  })

  // Build filter for server-side query
  const serverFilter = useMemo(() => {
    const filter: any = {
      projectId,
      floorId: filters.floorId || undefined,
    }
    if (filters.statuses.length > 0) {
      filter.status = filters.statuses
    }
    if (filters.priorities.length > 0) {
      filter.priority = filters.priorities
    }
    if (filters.infrastructureTypes.length > 0) {
      filter.infrastructureType = filters.infrastructureTypes
    }
    if (filters.searchQuery?.trim()) {
      filter.search = filters.searchQuery.trim()
    }
    return filter
  }, [projectId, filters])

  // Fetch snags data with pagination
  const { data, loading, error, refetch } = useQuery(Query.SNAGS_CONNECTION, {
    variables: {
      filter: serverFilter,
      page: currentPage,
      pageSize,
    },
    fetchPolicy: 'cache-and-network',
    skip: !projectId,
  })

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters.floorId, filters.statuses, filters.priorities, filters.infrastructureTypes, filters.searchQuery])

  // Mutations
  const [deleteSnag] = useMutation(Mutation.DELETE_SNAG, {
    onCompleted: () => {
      toastr.success('', t('Snags_deleted'))
      refetch()
    },
    onError: (error) => {
      toastr.error('', error.message)
    },
  })

  const [bulkDeleteSnags] = useMutation(Mutation.BULK_DELETE_SNAGS, {
    onCompleted: (data) => {
      const deletedCount = data?.bulkDeleteSnags?.length || 0
      toastr.success('', t('Snags_bulk_deleted', { count: deletedCount }))
      setSelection([])
      refetch()
    },
    onError: (error) => {
      toastr.error('', error.message)
    },
  })

  // Get connection data
  const connection = data?.snagsConnection
  const pageInfo = connection?.pageInfo
  const totalCount = connection?.totalCount || 0

  // Transform GraphQL data to grid format
  const snags: any[] =
    connection?.items?.map((snag: Snag) => ({
      id: snag.id,
      number: snag.number,
      displayNumber: snag.displayNumber,
      title: snag.title,
      description: snag.description || '',
      status: snag.status,
      priority: snag.priority,
      infrastructureType: snag.infrastructureType || '',
      buildingFloor: `${snag.floor.building.name} - ${snag.floor.name}`,
      assignedTo: snag.assignedTo?.name || '',
      dueDate: snag.dueDate,
      filesCount: snag.files?.length || 0,
      createdAt: snag.createdAt,
      hasPin: snag.pinX !== null && snag.pinY !== null,
      // Keep original data for editing
      _original: snag,
    })) || []

  const columns = [
    {
      title: t('Snags_table_number'),
      name: 'displayNumber',
      width: 100,
      sticky: true,
    },
    {
      title: t('Snags_table_status'),
      name: 'status',
      width: 120,
    },
    {
      title: t('Snags_table_priority'),
      name: 'priority',
      width: 100,
    },
    {
      title: t('Snags_table_title'),
      name: 'title',
      width: 250,
    },
    {
      title: t('Snags_table_infrastructure'),
      name: 'infrastructureType',
      width: 140,
    },
    {
      title: t('Snags_table_location'),
      name: 'buildingFloor',
      width: 200,
    },
    {
      title: t('Snags_table_assigned_to'),
      name: 'assignedTo',
      width: 150,
    },
    {
      title: t('Snags_table_due_date'),
      name: 'dueDate',
      width: 120,
    },
    {
      title: t('Snags_table_files'),
      name: 'filesCount',
      width: 80,
    },
    {
      title: t('Snags_table_pin'),
      name: 'hasPin',
      width: 70,
    },
  ]

  // Rows are now server-side filtered
  const rows = snags

  // Get sorted list of snag IDs for navigation (sorted by snag number)
  const filteredSnagIds = useMemo(() => {
    return [...rows]
      .sort((a, b) => (a.number || 0) - (b.number || 0))
      .map((row) => row.id)
  }, [rows])

  const handleNew = () => {
    setEditingSnagId(null)
    setModalOpen(true)
  }

  const handleEdit = (row: any) => {
    setEditingSnagId(row.id)
    setModalOpen(true)
  }

  const handleDelete = async (row: any) => {
    if (!window.confirm(t('Snags_delete_confirm'))) return
    await deleteSnag({ variables: { id: row.id } })
  }

  const handleBulkDelete = () => {
    if (selection.length === 0) return
    setDeleteConfirmModalOpen(true)
  }

  const handleConfirmBulkDelete = async () => {
    await bulkDeleteSnags({ variables: { ids: selection } })
    setDeleteConfirmModalOpen(false)
  }

  const handleExportSelected = () => {
    if (selection.length === 0) return
    setExportSelectedModalOpen(true)
  }

  const handleClearSelection = () => {
    setSelection([])
  }

  const handleModalClose = (saved?: boolean) => {
    setModalOpen(false)
    setEditingSnagId(null)
    if (saved) {
      refetch()
    }
  }

  const handleNavigateSnag = (snagId: string) => {
    setEditingSnagId(snagId)
  }

  const handleExportPdf = () => {
    if (snags.length === 0) {
      toastr.warning('', t('Snags_export_no_data'))
      return
    }
    setPdfExportModalOpen(true)
  }

  // Compute stats for current page (for display purposes)
  const snagStats = useMemo(() => {
    const stats = { total: totalCount, open: 0, inProgress: 0, resolved: 0, critical: 0 }
    snags.forEach((s: any) => {
      if (s.status === 'OPEN' || s.status === 'REOPENED') stats.open++
      else if (s.status === 'IN_PROGRESS') stats.inProgress++
      else if (s.status === 'RESOLVED' || s.status === 'VERIFIED' || s.status === 'CLOSED') stats.resolved++
      if (s.priority === 'CRITICAL') stats.critical++
    })
    return stats
  }, [snags, totalCount])

  const rowActions = [
    canEdit && {
      title: t('Common_edit'),
      action: handleEdit,
      color: 'info' as const,
    },
    canDelete && {
      title: t('Common_delete'),
      action: handleDelete,
      color: 'danger' as const,
    },
  ].filter(Boolean)

  const actionBar = {
    onDelete: selection.length > 0 ? handleBulkDelete : undefined,
    showSearch: false, // Search is now in SnagsFilterBar
  }

  const formatters = [
    {
      formatter: ({ value }: { value: string }) => {
        if (!value) return <ShadcnBadge variant="neutral" className="font-medium">-</ShadcnBadge>
        return (
          <ShadcnBadge variant={snagStatusVariant(value)} className="font-medium whitespace-nowrap">
            {t(`Snags_status_${value.toLowerCase()}`)}
          </ShadcnBadge>
        )
      },
      for: ['status'],
    },
    {
      formatter: ({ value }: { value: string }) => {
        if (!value) return <ShadcnBadge variant="neutral" className="font-medium">-</ShadcnBadge>
        return (
          <ShadcnBadge variant={snagPriorityVariant(value)} className="font-medium">
            {t(`Snags_priority_${value.toLowerCase()}`)}
          </ShadcnBadge>
        )
      },
      for: ['priority'],
    },
    {
      formatter: ({ value }: { value: string }) => {
        if (!value) return '-'
        const date = new Date(value)
        return date.toLocaleDateString(t('date_locale') || 'en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      },
      for: ['dueDate'],
    },
    {
      formatter: ({ value }: { value: number }) => (
        <span className={cn('text-sm', value > 0 ? 'font-medium' : 'text-muted-foreground')}>
          {value}
        </span>
      ),
      for: ['filesCount'],
    },
    {
      formatter: ({ value }: { value: boolean }) => (
        value
          ? <ShadcnBadge variant="success" className="text-xs">Yes</ShadcnBadge>
          : <span className="text-muted-foreground text-sm">No</span>
      ),
      for: ['hasPin'],
    },
    {
      formatter: ({ value }: { value: string }) => {
        if (!value || value.length <= 40) return value || '-'
        return (
          <span
            title={value}
            className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {value}
          </span>
        )
      },
      for: ['title', 'description'],
    },
  ]

  if (loading && !data) return <SnagsListSkeleton />
  if (error) return <div>{t('Snags_error_loading')}: {error.message}</div>

  return (
    <>
      {/* Snags Grid with integrated header */}
      <Card className="shadow-sm border-0">
        {/* Toolbar: Stats + Filters + Search + Actions */}
        <div className="py-2 flex items-center gap-3 flex-wrap">
          {/* Stats */}
          <div className="flex items-center gap-1">
            <span className="font-bold text-lg">{snagStats.total}</span>
            <span className="text-muted-foreground text-sm">{t('Common_total')}</span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
              <span className="text-xs text-muted-foreground">{snagStats.open}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
              <span className="text-xs text-muted-foreground">{snagStats.inProgress}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
              <span className="text-xs text-muted-foreground">{snagStats.resolved}</span>
            </span>
          </div>

          {/* Filters + Search (grows to fill) */}
          <div className="flex-1 min-w-0">
            <SnagsFilterBar
              filters={filters}
              onFiltersChange={setFilters}
              buildings={buildings}
              infrastructureTypes={infrastructureTypes}
              showBuildingSelector={true}
              showFloorSelector={true}
              showSearch={true}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {canExport && (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={handleExportPdf}
                disabled={snags.length === 0}
              >
                <FileText className="h-4 w-4" />
              </Button>
            )}
            {canCreate && (
              <Button
                size="sm"
                className="h-8"
                onClick={handleNew}
              >
                <Plus className="h-4 w-4" />
                {t('Snags_new_button')}
              </Button>
            )}
          </div>
        </div>

        {/* Bulk Action Bar - shows when items are selected */}
        {selection.length > 0 && (
          <div className="px-3 py-2 border-b flex items-center justify-between bg-blue-50">
            <div className="flex items-center">
              <span className="font-bold mr-2 text-sm">
                {t('Snags_selected_count', { count: selection.length })}
              </span>
              <CompatButton
                size="sm"
                color="link"
                className="text-muted-foreground p-0 ml-2"
                onClick={handleClearSelection}
              >
                <X className="h-4 w-4" />
                {t('Common_clear_selection')}
              </CompatButton>
            </div>
            <div className="flex items-center gap-2">
              {canExport && (
                <CompatButton
                  size="sm"
                  color="primary"
                  onClick={handleExportSelected}
                >
                  <FileText className="h-4 w-4" />
                  {t('Snags_export_selected')}
                </CompatButton>
              )}
              {canDelete && (
                <CompatButton
                  size="sm"
                  color="danger"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="h-4 w-4" />
                  {t('Snags_delete_selected')}
                </CompatButton>
              )}
            </div>
          </div>
        )}

        <CardBody className="p-0 relative min-h-[200px]">
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
              <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          <Grid
            columns={columns}
            rows={rows}
            selection={selection}
            onSelectionChange={setSelection}
            rowActions={rowActions}
            actionBar={actionBar}
            formatters={formatters}
            showHeader={true}
            sorting={true}
            selectByRowClick={false}
            showSelectionColumn={true}
            defaultSorting={[{ columnName: 'displayNumber', direction: 'asc' }]}
            noDataMessage={t('Snags_table_no_data')}
            onRowClick={(row: any) => {
              setEditingSnagId(row.id)
              setModalOpen(true)
            }}
          />
        </CardBody>

        {/* Pagination */}
        {pageInfo && pageInfo.totalPages > 1 && (
          <div className="px-3 py-2 border-t flex items-center justify-between">
            <div className="text-muted-foreground small">
              {t('Snags_showing', {
                from: (pageInfo.currentPage - 1) * pageInfo.pageSize + 1,
                to: Math.min(pageInfo.currentPage * pageInfo.pageSize, totalCount),
                total: totalCount,
              })}
            </div>
            <Pagination size="sm" className="mb-0">
              <PaginationItem disabled={!pageInfo.hasPreviousPage}>
                <PaginationLink
                  previous
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className={pageInfo.hasPreviousPage ? 'cursor-pointer' : 'cursor-not-allowed'}
                >
                  <ChevronLeft className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>

              {/* First page */}
              {pageInfo.currentPage > 2 && (
                <PaginationItem>
                  <PaginationLink onClick={() => setCurrentPage(1)}>1</PaginationLink>
                </PaginationItem>
              )}

              {/* Ellipsis if needed */}
              {pageInfo.currentPage > 3 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

              {/* Previous page */}
              {pageInfo.currentPage > 1 && (
                <PaginationItem>
                  <PaginationLink onClick={() => setCurrentPage(currentPage - 1)}>
                    {pageInfo.currentPage - 1}
                  </PaginationLink>
                </PaginationItem>
              )}

              {/* Current page */}
              <PaginationItem active>
                <PaginationLink>{pageInfo.currentPage}</PaginationLink>
              </PaginationItem>

              {/* Next page */}
              {pageInfo.currentPage < pageInfo.totalPages && (
                <PaginationItem>
                  <PaginationLink onClick={() => setCurrentPage(currentPage + 1)}>
                    {pageInfo.currentPage + 1}
                  </PaginationLink>
                </PaginationItem>
              )}

              {/* Ellipsis if needed */}
              {pageInfo.currentPage < pageInfo.totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

              {/* Last page */}
              {pageInfo.currentPage < pageInfo.totalPages - 1 && (
                <PaginationItem>
                  <PaginationLink onClick={() => setCurrentPage(pageInfo.totalPages)}>
                    {pageInfo.totalPages}
                  </PaginationLink>
                </PaginationItem>
              )}

              <PaginationItem disabled={!pageInfo.hasNextPage}>
                <PaginationLink
                  next
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className={pageInfo.hasNextPage ? 'cursor-pointer' : 'cursor-not-allowed'}
                >
                  <ChevronRight className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>
            </Pagination>
          </div>
        )}
      </Card>

      {modalOpen && (
        <SnagModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          snagId={editingSnagId}
          floorId={filters.floorId}
          onChangePinLocation={handleChangePinLocation}
          canChangePinLocation={true}
          filteredSnagIds={filteredSnagIds}
          onNavigate={handleNavigateSnag}
          onShowOnBlueprint={handleShowOnBlueprint}
        />
      )}

      {projectId && (
        <PdfExportModal
          isOpen={pdfExportModalOpen}
          toggle={() => setPdfExportModalOpen(false)}
          projectId={projectId}
          filter={{
            buildingId: filters.buildingId,
            floorId: filters.floorId,
            status: filters.statuses.length > 0 ? filters.statuses : undefined,
            priority: filters.priorities.length > 0 ? filters.priorities : undefined,
            infrastructureType: filters.infrastructureTypes.length > 0 ? filters.infrastructureTypes : undefined,
            search: filters.searchQuery || undefined,
          }}
        />
      )}

      {/* Export Selected Modal */}
      {projectId && (
        <PdfExportModal
          isOpen={exportSelectedModalOpen}
          toggle={() => setExportSelectedModalOpen(false)}
          projectId={projectId}
          snagIds={selection}
          selectedCount={selection.length}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmModalOpen} toggle={() => setDeleteConfirmModalOpen(false)} centered>
        <ModalHeader toggle={() => setDeleteConfirmModalOpen(false)}>
          <Trash2 className="h-4 w-4 mr-2 text-destructive inline-block" />
          {t('Snags_delete_confirm_title')}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {t('Snags_bulk_delete_confirm', { count: selection.length })}
          </p>
        </ModalBody>
        <ModalFooter>
          <CompatButton color="secondary" onClick={() => setDeleteConfirmModalOpen(false)}>
            {t('Common_cancel')}
          </CompatButton>
          <CompatButton color="danger" onClick={handleConfirmBulkDelete}>
            <Trash2 className="h-4 w-4" />
            {t('Common_delete')}
          </CompatButton>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default SnagsList
